import React, { useEffect, useState } from "react";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

const WeClappCustomersPage = () => {
    const [allCustomers, setAllCustomers] = useState([]); // All loaded customers
    const [displayCustomers, setDisplayCustomers] = useState([]); // Customers to display on current page
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(500);
    const [filter, setFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [importing, setImporting] = useState({});
    const [slotifyCustomers, setSlotifyCustomers] = useState({}); // Map of weclapp_id -> slotify customer

    const Auth = useAuthentication();
    const CurrentUser = useSelector((state) => state.CurrentUser);
    const history = useHistory();

    useEffect(() => {
        loadAllCustomers();
        loadSlotifyCustomers();
    }, []);

    useEffect(() => {
        filterAndPaginate();
    }, [allCustomers, filter, currentPage]);

    const loadSlotifyCustomers = () => {
        // Load all Slotify customers with weclapp_customer_id
        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + "expenseProjects/byParent",
            auth: Auth.getAuthObject(),
            params: {
                parent: 1
            }
        }).then(response => {
            const customersMap = {};
            if (response.data && response.data.children) {
                response.data.children.forEach(customer => {
                    if (customer.weclapp_customer_id && customer.project_type === 'customer') {
                        customersMap[customer.weclapp_customer_id] = customer;
                    }
                });
            }
            setSlotifyCustomers(customersMap);
        }).catch(error => {
            console.error("Error loading Slotify customers:", error);
        });
    };

    const loadAllCustomers = async () => {
        setLoading(true);
        let allResults = [];
        let currentPage = 1;
        const pageSize = 500; // Larger page size for fetching
        let hasMore = true;

        try {
            while (hasMore) {
                const response = await axios({
                    method: "get",
                    url: SyncConstants["syncBaseURL"] + "weclapp/customers",
                    auth: Auth.getAuthObject(),
                    params: {
                        page: currentPage,
                        pageSize: pageSize,
                        filter: filter || undefined
                    }
                });

                if (response.data.success && response.data.data.result) {
                    const results = response.data.data.result;
                    allResults = [...allResults, ...results];

                    // Check if there are more pages
                    if (results.length < pageSize) {
                        hasMore = false;
                    } else {
                        currentPage++;
                    }
                } else {
                    hasMore = false;
                }

                // Safety limit to prevent infinite loops
                if (currentPage > 50) {
                    hasMore = false;
                }
            }

            setAllCustomers(allResults);
        } catch (error) {
            console.error("Error loading WeClapp customers:", error);
        } finally {
            setLoading(false);
        }
    };

    const filterAndPaginate = () => {
        let filtered = allCustomers;

        // Apply client-side filter
        if (filter) {
            const filterLower = filter.toLowerCase();
            filtered = allCustomers.filter(customer => {
                const customerName = getCustomerName(customer).toLowerCase();
                const customerNumber = (customer.customerNumber || '').toLowerCase();
                const email = (customer.email || '').toLowerCase();

                return customerName.includes(filterLower) ||
                       customerNumber.includes(filterLower) ||
                       email.includes(filterLower);
            });
        }

        // Paginate
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setDisplayCustomers(filtered.slice(startIndex, endIndex));
    };

    const importCustomer = (customer) => {
        setImporting({ ...importing, [customer.id]: true });

        const customerName = customer.company ||
                           (customer.firstName && customer.lastName ?
                            `${customer.firstName} ${customer.lastName}` :
                            customer.customerNumber);

        axios({
            method: "post",
            url: SyncConstants["syncBaseURL"] + "weclapp/customer/import",
            auth: Auth.getAuthObject(),
            data: {
                weclappCustomerId: customer.id,
                customerName: customerName
            }
        }).then(response => {
            if (response.data.success) {
                alert(`Kunde "${customerName}" wurde erfolgreich in Slotify angelegt!`);
                // Reload slotify customers to update the status
                loadSlotifyCustomers();
            } else {
                alert("Fehler: " + response.data.error);
            }
            setImporting({ ...importing, [customer.id]: false });
        }).catch(error => {
            console.error("Error importing customer:", error);
            alert("Fehler beim Import: " + (error.response?.data?.error || error.message));
            setImporting({ ...importing, [customer.id]: false });
        });
    };

    const showInvoices = (customerId) => {
        history.push(`/invoices?customer=${customerId}`);
    };

    const showExpenses = (customerId) => {
        history.push(`/expenses/${customerId}`);
    };

    const getCustomerName = (customer) => {
        if (customer.company) return customer.company;
        if (customer.firstName && customer.lastName) {
            return `${customer.firstName} ${customer.lastName}`;
        }
        return customer.customerNumber || 'Unbekannt';
    };

    const handleFilterChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearch = () => {
        setFilter(searchInput);
        setCurrentPage(1); // Reset to first page when filtering
    };

    // Check if user is administrator
    if (!CurrentUser.data || CurrentUser.data.role !== 'administrator') {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">
                    <h4>Zugriff verweigert</h4>
                    <p>Sie benötigen Administratorrechte, um auf dieses Modul zugreifen zu können.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <h2>WeClapp-Kunden</h2>
            <p className="text-muted">
                Importieren Sie Kunden aus WeClapp in Slotify. Es wird automatisch ein Kundenprojekt und ein Service-Auftrag angelegt.
            </p>

            <div className="row mb-4 mt-4">
                <div className="col-md-8">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nach Kunden suchen (Name, Kundennummer, E-Mail...)"
                            value={searchInput}
                            onChange={handleFilterChange}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                        />
                        <button
                            className="btn btn-primary"
                            onClick={handleSearch}
                        >
                            <i className="bi bi-search"></i> Suchen
                        </button>
                        {filter && (
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setFilter('');
                                    setSearchInput('');
                                    setCurrentPage(1);
                                }}
                            >
                                <i className="bi bi-x"></i> Zurücksetzen
                            </button>
                        )}
                    </div>
                    <small className="form-text text-muted">
                        Die Suche durchsucht alle Kunden in WeClapp. {allCustomers.length} Kunden geladen
                        {filter && ` - ${displayCustomers.length > 0 ? Math.ceil(allCustomers.filter(c => {
                            const filterLower = filter.toLowerCase();
                            const customerName = getCustomerName(c).toLowerCase();
                            const customerNumber = (c.customerNumber || '').toLowerCase();
                            const email = (c.email || '').toLowerCase();
                            return customerName.includes(filterLower) || customerNumber.includes(filterLower) || email.includes(filterLower);
                        }).length / itemsPerPage) * itemsPerPage : 0} Treffer gefunden`}
                    </small>
                </div>
            </div>

            {loading && (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Lade Kunden...</span>
                    </div>
                    <p className="mt-3">Lade alle Kunden aus WeClapp...</p>
                </div>
            )}

            {!loading && displayCustomers.length > 0 && (
                <>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{width: '100px'}}>Status</th>
                                    <th style={{width: '120px'}}>Kundennr.</th>
                                    <th>Name</th>
                                    <th>Kontakt</th>
                                    <th style={{width: '280px'}} className="text-end">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayCustomers.map(customer => {
                                    const isInSlotify = slotifyCustomers[customer.id];
                                    return (
                                        <tr key={customer.id}>
                                            <td>
                                                {isInSlotify ? (
                                                    <span className="badge bg-success">
                                                        <i className="bi bi-check-circle me-1"></i>
                                                        In Slotify
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary">
                                                        <i className="bi bi-dash-circle me-1"></i>
                                                        Nicht importiert
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-dark border">
                                                    {customer.customerNumber || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <div>
                                                    <strong className="d-block">{getCustomerName(customer)}</strong>
                                                    {customer.company2 && (
                                                        <small className="text-muted d-block">{customer.company2}</small>
                                                    )}
                                                    {customer.addresses && customer.addresses[0] && (
                                                        <small className="text-muted">
                                                            <i className="bi bi-geo-alt me-1"></i>
                                                            {customer.addresses[0].city}
                                                        </small>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    {customer.email && (
                                                        <div className="mb-1">
                                                            <i className="bi bi-envelope me-1"></i>
                                                            <a href={`mailto:${customer.email}`}>{customer.email}</a>
                                                        </div>
                                                    )}
                                                    {(customer.phone || customer.mobilePhone1) && (
                                                        <div>
                                                            <i className="bi bi-telephone me-1"></i>
                                                            {customer.phone || customer.mobilePhone1}
                                                        </div>
                                                    )}
                                                    {!customer.email && !customer.phone && !customer.mobilePhone1 && (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                {!isInSlotify ? (
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => importCustomer(customer)}
                                                        disabled={importing[customer.id]}
                                                        title="Kunde in Slotify importieren"
                                                    >
                                                        {importing[customer.id] ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-1"></span>
                                                                Importiere...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-download me-1"></i>
                                                                Importieren
                                                            </>
                                                        )}
                                                    </button>
                                                ) : (
                                                    <span className="text-success small">
                                                        <i className="bi bi-check-circle-fill me-1"></i>
                                                        Bereits importiert
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                            {filter ? (
                                <>
                                    Zeige {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, allCustomers.filter(c => {
                                        const filterLower = filter.toLowerCase();
                                        const customerName = getCustomerName(c).toLowerCase();
                                        const customerNumber = (c.customerNumber || '').toLowerCase();
                                        const email = (c.email || '').toLowerCase();
                                        return customerName.includes(filterLower) || customerNumber.includes(filterLower) || email.includes(filterLower);
                                    }).length)} von {allCustomers.filter(c => {
                                        const filterLower = filter.toLowerCase();
                                        const customerName = getCustomerName(c).toLowerCase();
                                        const customerNumber = (c.customerNumber || '').toLowerCase();
                                        const email = (c.email || '').toLowerCase();
                                        return customerName.includes(filterLower) || customerNumber.includes(filterLower) || email.includes(filterLower);
                                    }).length} Treffern
                                </>
                            ) : (
                                <>
                                    Zeige {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, allCustomers.length)} von {allCustomers.length} Kunden
                                </>
                            )}
                        </div>
                        <div>
                            <button
                                className="btn btn-outline-primary me-2"
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                            >
                                <i className="bi bi-chevron-left"></i> Zurück
                            </button>
                            <span className="mx-2">Seite {currentPage}</span>
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={displayCustomers.length < itemsPerPage}
                            >
                                Weiter <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                </>
            )}

            {!loading && allCustomers.length === 0 && (
                <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    Keine Kunden gefunden
                </div>
            )}

            {!loading && allCustomers.length > 0 && displayCustomers.length === 0 && filter && (
                <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Keine Kunden gefunden, die "{filter}" entsprechen. Versuchen Sie eine andere Suche.
                </div>
            )}
        </div>
    );
};

export default WeClappCustomersPage;
